import {Tooltip} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {ElevationApp} from "../../ElevationApp";
import {RibbonCheckbox, RibbonGroup, RibbonSelect, RibbonStack} from "../Ribbon";
import {DimensionField} from "../../components/DimensionField";
import {
    beadConflictsWithThermal,
    glassBeadLabels,
    glassTypeLabels,
    MdGlass,
    MdGlassBead,
    MdGlassResolved,
    MdGlassType,
} from "../../model/glass/MdGlass";

const TYPE_OPTIONS = (Object.keys(glassTypeLabels) as MdGlassType[])
    .map(t => ({value: t as string, label: glassTypeLabels[t]}));

const BEAD_OPTIONS = (Object.keys(glassBeadLabels) as MdGlassBead[])
    .map(b => ({value: b as string, label: glassBeadLabels[b] + " Bead"}));

// the per-field value shared by every lite in the selection, or undefined for a field
// the lites disagree on (shown blank / unchecked).
export type GlassShared = {[K in keyof MdGlassResolved]: MdGlassResolved[K] | undefined};

export function sharedGlass(list: MdGlassResolved[]): GlassShared {
    const shared = <K extends keyof MdGlassResolved>(key: K) =>
        list.every(g => g[key] === list[0][key]) ? list[0][key] : undefined;

    return {
        thermal: shared("thermal"),
        type: shared("type"),
        thickness: shared("thickness"),
        bead: shared("bead"),
        undersize: shared("undersize"),
    };
}

// true when any lite in the list is a thermal unit held by a non-thermal bead.
export function anyBeadConflict(list: MdGlassResolved[]): boolean {
    return list.some(beadConflictsWithThermal);
}

// the "Glass" ribbon group shared by the frame-lite and door-lite tabs. Reads the
// selection's shared values and hands each edit back as a patch; the caller folds it
// over the selection with withGlassEdit so the thermal rules apply per lite.
export function GlassControls(props: {
    app: ElevationApp;
    glass: GlassShared;
    // true when any selected lite is a thermal unit held by a non-thermal bead.
    beadConflict: boolean;
    onChange: (patch: MdGlass) => void;
}) {
    const sys = props.app.project?.unitSystem || "imperial";
    const g = props.glass;

    return (
        <RibbonGroup label="Glass">
            <RibbonStack>
                <DimensionField
                    label="Thickness"
                    value={g.thickness ?? 0}
                    sys={sys}
                    onChange={thickness => props.onChange({thickness})}
                />
                <DimensionField
                    label="Undersize"
                    value={g.undersize ?? 0}
                    sys={sys}
                    onChange={undersize => props.onChange({undersize})}
                />
            </RibbonStack>

            <RibbonStack>
                <RibbonSelect
                    stacked
                    value={g.type ?? ""}
                    options={TYPE_OPTIONS}
                    onChange={value => props.onChange({type: value as MdGlassType})}
                />
                <div style={{display: "flex", alignItems: "center", gap: 4}}>
                    <RibbonSelect
                        stacked
                        value={g.bead ?? ""}
                        options={BEAD_OPTIONS}
                        onChange={value => props.onChange({bead: value as MdGlassBead})}
                    />
                    {props.beadConflict && (
                        <Tooltip title="A thermal unit is normally held by a thermal bead" arrow>
                            <WarningAmberIcon sx={{color: "warning.main", fontSize: 20}} />
                        </Tooltip>
                    )}
                </div>
            </RibbonStack>

            <RibbonStack>
                <RibbonCheckbox
                    label="Thermal"
                    checked={!!g.thermal}
                    onChange={thermal => props.onChange({thermal})}
                />
            </RibbonStack>
        </RibbonGroup>
    );
}
